import {
  useCreateHighlight,
  useGetHighlightById,
  useUpdateHighlight,
} from '@/api/hooks/useHighLights';
import {
  HighlightsFormValues,
  useHighlightsForm,
} from '@/hooks/usehighlightForm';
import { useHighlightStore } from '@/store/Highlight/HighlightStore';
import { useState } from 'react';
import { Editor as TinyMCEEditor } from 'tinymce';
import { useToast } from '@/hooks/useToast';

export const useHighlights = () => {
  const { mutate: createHighlight } = useCreateHighlight();
  const [plainText, setPlainText] = useState('');
  const [isEdit, setIsEdit] = useState(false);

  const {
    register,
    handleSubmit,
    errors,
    setValue,
    reset,
    control,
    getValues,
  } = useHighlightsForm();

  const {
    openHighlightModal,
    isOpenHighlightModal,
    highlightId,
    setHighlightId,
  } = useHighlightStore();

  const { data: highlightData, isLoading: addHighlightLoading } =
    useGetHighlightById(highlightId);

  const { mutate: updateHighlight, isPending: updateHighlightLoading } =
    useUpdateHighlight();

  const { showToast } = useToast();
  const handleClose = () => {
    openHighlightModal();
    setHighlightId('');
    setIsEdit(false);
    reset({ term: '', definition: '' });
  };

  const onSubmit = (data: HighlightsFormValues) => {
    createHighlight(
      {
        definition: {
          html: data.definition,
          text: plainText,
        },
        term: data.term,
        uuid: crypto.randomUUID(),
      },
      {
        onSuccess: () => {
          showToast('Mot ajouté avec succès', 'success', {
            autoClose: 5000,
            position: 'top-right',
          });
          handleClose();
        },
        onError: (error) => {
          showToast(error.message, 'error', {
            autoClose: 5000,
            position: 'top-right',
          });
        },
      },
    );
  };

  const handleUpdate = (data: HighlightsFormValues) => {
    updateHighlight(
      {
        id: highlightId,
        input: {
          definition: {
            html: data.definition,
            text: plainText,
          },
          term: data.term,
          uuid: highlightData?.uuid || '',
        },
      },
      {
        onSuccess: () => {
          showToast('Mot modifié avec succès', 'success', {
            autoClose: 5000,
            position: 'top-right',
          });
          handleClose();
        },
        onError: (error) => {
          showToast(error.message, 'error', {
            autoClose: 5000,
            position: 'top-right',
          });
        },
      },
    );
  };

  const handleEditorChange = (newValues: string, editor: TinyMCEEditor) => {
    const plainText = editor.getContent({ format: 'text' });
    setPlainText(plainText);
    setValue('definition', newValues, {
      shouldValidate: true,
    });
  };

  return {
    register,
    handleSubmit,
    errors,
    onSubmit,
    handleEditorChange,
    setValue,
    getValues,
    isOpenHighlightModal,
    openHighlightModal,
    setHighlightId,
    handleClose,
    highlightData,
    isEdit,
    setIsEdit,
    handleUpdate,
    updateHighlightLoading,
    addHighlightLoading,
    control,
  };
};
